import { useState } from 'react';
import { Database, Code2, AlertCircle, Copy, ExternalLink } from 'lucide-react';
import type { RootCauseAnalysis } from '../../services/issueAnalysisApi';
import { AdaptedScriptViewer } from './AdaptedScriptViewer';

interface SqlScriptSolution {
  id: number;
  original_jira?: string | null;
  issue_summary: string;
  root_cause: string;
  impact_description: string;
  sql_script: string;
  screen_name?: string | null;
  module_category?: string | null;
}

interface SqlScriptWithFitness extends SqlScriptSolution {
  fitness_score: number;
  confidence_level: string;
  matched_critical_fields: string[];
  matched_supporting_fields: string[];
}

interface ApplicableSqlScriptsProps {
  applicableScripts: SqlScriptSolution[];
  rootCauseAnalysis: RootCauseAnalysis;
  issueId?: number;
  extractedEntities?: Array<{ entity_type: string; value: string; confidence: number }>;
  relevantTables?: Array<{ name: string; description: string; fields: Array<{ name: string; field_type: string; description: string; nullable: boolean; constraints: string[]; examples: string[] }> }>;
  detectedModules?: string[];
}

export function ApplicableSqlScripts({
  applicableScripts,
  rootCauseAnalysis,
  issueId = 0,
  extractedEntities = [],
  relevantTables = [],
  detectedModules = [],
}: ApplicableSqlScriptsProps) {
  const [expanded, setExpanded] = useState(applicableScripts.length > 0);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50/30">
      <button
        onClick={handleToggle}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-blue-50/60 transition-colors"
      >
        <div className="text-blue-600">
          {expanded ? '▼' : '▶'}
        </div>
        <Database size={14} className="text-blue-600" />
        <span className="text-xs font-semibold text-gray-800 flex-1">Recommended SQL Scripts (Temporary Fixes)</span>
        {applicableScripts.length > 0 && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold">
            {applicableScripts.length} found
          </span>
        )}
        {applicableScripts.length === 0 && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-semibold">
            None found
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-2 border-t border-blue-200 space-y-2">
          {applicableScripts.length > 0 ? (
            <>
              <div className="bg-blue-50 p-2 rounded text-xs text-blue-700 border border-blue-100 mb-2">
                <strong>Important:</strong> L3 support must hand-fill <code className="font-mono">?</code> placeholders with actual values and review the script before running in pre-production.
              </div>
              {applicableScripts.map((script) => (
                <SqlScriptCard
                  key={script.id}
                  script={script}
                  issueId={issueId}
                  problemSummary={script.issue_summary}
                  rootCause={script.root_cause}
                  impactDescription={script.impact_description}
                  extractedEntities={extractedEntities}
                  moduleCategory={script.module_category || 'Unknown'}
                  relevantTables={relevantTables}
                  detectedModules={detectedModules}
                />
              ))}
            </>
          ) : (
            <div className="bg-gray-50 p-3 rounded text-xs text-gray-600 border border-gray-200">
              <AlertCircle className="inline mr-2" size={14} />
              <span>No recommended SQL scripts found. Semantic matching did not identify any similar past issues. This may be a novel issue type — consider escalating to the development team or consulting the knowledge base for similar scenarios.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface SqlScriptCardProps {
  script: SqlScriptSolution | SqlScriptWithFitness;
  issueId: number;
  problemSummary: string;
  rootCause: string;
  impactDescription: string;
  extractedEntities: Array<{ entity_type: string; value: string; confidence: number }>;
  moduleCategory: string;
  relevantTables: Array<{ name: string; description: string; fields: Array<{ name: string; field_type: string; description: string; nullable: boolean; constraints: string[]; examples: string[] }> }>;
  detectedModules?: string[];
}

function SqlScriptCard({
  script,
  issueId,
  problemSummary,
  rootCause,
  impactDescription,
  extractedEntities,
  moduleCategory,
  relevantTables,
  detectedModules = [],
}: SqlScriptCardProps) {
  const [showScript, setShowScript] = useState(false);
  const [showAdapter, setShowAdapter] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyScript = () => {
    navigator.clipboard.writeText(script.sql_script);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isFitnessScript = (s: SqlScriptSolution | SqlScriptWithFitness): s is SqlScriptWithFitness => {
    return 'fitness_score' in s;
  };

  const hasFitness = isFitnessScript(script);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-2 flex-1">
            <Code2 size={12} className="text-gray-500 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800">Template {script.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasFitness && (
              <div className="flex items-center gap-1">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap ${
                  script.fitness_score >= 80
                    ? 'bg-green-100 text-green-700'
                    : script.fitness_score >= 65
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {script.fitness_score.toFixed(0)}% fit
                </span>
              </div>
            )}
            {script.module_category && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded whitespace-nowrap">
                {script.module_category}
              </span>
            )}
          </div>
        </div>
        {hasFitness && (
          <div className="text-xs text-gray-600">
            <span className="font-semibold">Matched fields:</span> {[
              ...script.matched_critical_fields.map(f => `${f} (critical)`),
              ...script.matched_supporting_fields.map(f => `${f} (support)`)
            ].join(', ')}
          </div>
        )}
      </div>

      <div className="px-3 py-2 space-y-2">
        {script.root_cause && (
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Root Cause:</p>
            <p className="text-xs text-gray-600">{script.root_cause}</p>
          </div>
        )}

        {script.impact_description && (
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1">Impact:</p>
            <p className="text-xs text-gray-600">{script.impact_description}</p>
          </div>
        )}

        <button
          onClick={() => setShowScript(!showScript)}
          className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 mt-2"
        >
          {showScript ? '▼ Hide' : '▶ Show'} SQL Script
        </button>

        {showScript && (
          <div className="mt-2 space-y-2">
            <div className="p-2 bg-gray-900 rounded border border-gray-700 overflow-x-auto">
              <pre className="text-xs text-gray-100 font-mono">{script.sql_script}</pre>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopyScript}
                className="flex-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold px-2 py-1.5 rounded flex items-center justify-center gap-1 transition-colors"
              >
                <Copy size={11} />
                {copied ? 'Copied!' : 'Copy SQL Script'}
              </button>
            </div>

            {/* Adapt Script Section */}
            {!showAdapter && (
              <button
                onClick={() => setShowAdapter(true)}
                className="w-full text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1.5 rounded hover:bg-blue-50 transition-colors"
              >
                ✨ Intelligent Adaptation (AI)
              </button>
            )}

            {showAdapter && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <AdaptedScriptViewer
                  issueId={issueId}
                  scriptId={script.id}
                  templateScript={script.sql_script}
                  problemSummary={problemSummary}
                  rootCause={rootCause}
                  impactDescription={impactDescription}
                  extractedEntities={extractedEntities}
                  criticalFields={isFitnessScript(script) ? script.matched_critical_fields : []}
                  supportingFields={isFitnessScript(script) ? script.matched_supporting_fields : []}
                  relevantTables={relevantTables}
                  moduleCategory={moduleCategory}
                  detectedModules={detectedModules}
                  onClose={() => setShowAdapter(false)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
